import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import {
  beginCognitoSignIn,
  buildCognitoLogoutUrl,
  completeCognitoSignIn,
  getCognitoConfig,
  isCognitoConfigured,
} from './cognito';
import { setAuthTokenProvider } from '../api/client';
import { lockAllClients } from '../crypto/sessionKeyStore';

const TOKEN_STORAGE_KEY = 'amandamd_auth_tokens_v1';
const EXPIRY_SKEW_SECONDS = 30;

const AuthContext = createContext(null);

function readStoredTokens() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(TOKEN_STORAGE_KEY);
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw);
    if (!parsed?.accessToken || !parsed?.expiresAt) {
      return null;
    }

    const nowSeconds = Math.floor(Date.now() / 1000);
    if ((parsed.expiresAt - EXPIRY_SKEW_SECONDS) <= nowSeconds) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function persistTokens(tokens) {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  window.sessionStorage.setItem(TOKEN_STORAGE_KEY, JSON.stringify(tokens));
}

function clearPersistedTokens() {
  if (typeof window === 'undefined' || !window.sessionStorage) {
    return;
  }
  window.sessionStorage.removeItem(TOKEN_STORAGE_KEY);
}

function fallbackUserFromTokens(tokens) {
  return {
    email: tokens?.email || null,
    subject: tokens?.subject || null,
  };
}

export function AuthProvider({ children }) {
  // Initialize tokens and set auth provider synchronously to avoid race conditions
  const [tokens, setTokens] = useState(() => {
    const stored = readStoredTokens();
    // Set provider immediately during initialization - Cognito authorizers require ID token
    if (stored?.idToken) {
      setAuthTokenProvider(async () => stored.idToken);
    }
    return stored;
  });
  const [authLoading, setAuthLoading] = useState(false);
  const config = useMemo(() => getCognitoConfig(), []);
  const configured = isCognitoConfigured(config);

  // Also update provider when tokens change (for cleanup on sign out)
  useEffect(() => {
    return () => setAuthTokenProvider(null);
  }, []);

  const signIn = async (postLoginPath = '/') => {
    if (!configured) {
      throw new Error('Cognito is not configured in frontend environment.');
    }
    await beginCognitoSignIn(config, postLoginPath);
  };

  const completeSignIn = async (code, state) => {
    if (!configured) {
      throw new Error('Cognito is not configured in frontend environment.');
    }

    setAuthLoading(true);
    try {
      const result = await completeCognitoSignIn(config, code, state);
      // Set auth provider BEFORE updating state to avoid race with API calls
      if (result.tokens?.idToken) {
        setAuthTokenProvider(async () => result.tokens.idToken);
      }
      setTokens(result.tokens);
      persistTokens(result.tokens);
      return result.postLoginPath || '/';
    } finally {
      setAuthLoading(false);
    }
  };

  const signOut = () => {
    clearPersistedTokens();
    setTokens(null);
    lockAllClients();

    if (configured) {
      window.location.assign(buildCognitoLogoutUrl(config));
    }
  };

  const value = {
    configured,
    authLoading,
    isAuthenticated: Boolean(tokens?.idToken),
    tokens,
    user: fallbackUserFromTokens(tokens),
    signIn,
    completeSignIn,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
